import { useEffect, useState } from 'react';
import { api, type Asset, type Brand, type Clip, type ClipBeat, type Idea, type ModelInfo, type TalentRow } from '../lib/api';
import { chooseModel, readLast } from '../lib/models';
import { LOOK_EVERY_MS, GIVE_UP_AFTER, GAVE_UP, useElapsed, saySeconds } from '../lib/waiting';
import { SceneBuilder } from './SceneBuilder';

// The Video Lab (197): the cast in, a clip out.
//
// "Ralph, Shirley and Timmy eating dinner, fifteen seconds." The first
// bench makes that the ILLUSTRATED way: each beat is a still drawn with the
// named talent as references — the same call that keeps a character's blue
// shirt — moved by the render plane, spoken in their own voices, a bed
// under it, joins between. Three pictures and some voice characters: about
// 850 credits. A generated clip of the same length is 6,000–22,500, three to
// nine whole episodes, and that bench comes next, behind a quote.
//
// The picker on the left is the cast, as on the Audio Lab: the people
// persist here, which is what a generic tool does not have.

type Bench = 'illustrated' | 'generative' | 'story';
const MAX_BEATS = 6;
const MAX_PEOPLE = 3;
const MOTIONS: Array<{ id: NonNullable<ClipBeat['motion']>; label: string }> = [
  { id: 'zoom-in', label: 'push in' }, { id: 'zoom-out', label: 'pull out' }, { id: 'pan-left', label: 'pan left' }, { id: 'pan-right', label: 'pan right' }, { id: 'none', label: 'hold' },
];

interface Draft { talentId: string; text: string; read: string; scene: string; motion: NonNullable<ClipBeat['motion']> }
const blank = (talentId = ''): Draft => ({ talentId, text: '', read: '', scene: '', motion: 'zoom-in' });

export function VideoLab({ brand, prefill, onUsedPrefill }: {
  brand: Brand | null;
  /** 200: a proposal the Idea Lab handed over when the creator accepted it. */
  prefill?: Idea | null;
  onUsedPrefill?: () => void;
}) {
  // A prefill always means scenes, so open on that bench rather than making
  // the creator find it after they already said yes.
  const [bench, setBench] = useState<Bench>(prefill ? 'story' : 'illustrated');
  useEffect(() => { if (prefill) setBench('story'); }, [prefill]);
  const [talent, setTalent] = useState<TalentRow[]>([]);
  const [beds, setBeds] = useState<Asset[]>([]);
  const [clips, setClips] = useState<Clip[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // 204: the picture price for the illustrated bench is the workspace's picture model's, from the catalogue;
  // null until it answers, and the quote says so rather than showing a number nobody is billed.
  const [imageModels, setImageModels] = useState<ModelInfo[] | null>(null);
  useEffect(() => {
    let live = true;
    api.models('image').then((m) => { if (live) setImageModels(m); }).catch(() => {});
    return () => { live = false; };
  }, []);
  const creditsPerPicture = imageModels ? chooseModel(imageModels, readLast('image'))?.credits ?? null : null;

  useEffect(() => {
    let live = true;
    api.talent().then((r) => { if (live) setTalent(r.talent); }).catch(() => {});
    api.assets(brand?.id ?? null).then((all) => { if (live) setBeds(all.filter((a) => a.kind === 'audio' && a.status === 'ready')); }).catch(() => {});
    return () => { live = false; };
  }, [brand?.id]);

  // What this bench has built. A clip is building until the render plane
  // has drawn, spoken and cut it — a few minutes, longer from cold — so the
  // list is watched while any is, with a visible give-up rather than a
  // spinner that lies.
  async function loadClips() {
    try { setClips(await api.clips(brand?.id ?? null)); } catch (e) { setError((e as Error).message); }
  }
  useEffect(() => { void loadClips(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [brand?.id]);
  const [stalled, setStalled] = useState(false);
  const building = clips.some((c) => c.status === 'building');
  useEffect(() => {
    if (!building) { setStalled(false); return; }
    let looks = 0;
    const t = setInterval(() => { if (looks++ >= GIVE_UP_AFTER) { clearInterval(t); setStalled(true); return; } void loadClips(); }, LOOK_EVERY_MS);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [building]);

  return (
    <section data-tool="video-lab" style={{ display: 'grid', gap: 14 }}>
      <div className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>The Video Lab</h3>
        <span className="sub-dim">the cast in a scene, spoken in their voices — into the library{brand ? `, for ${brand.name}` : ''}</span>
        <span style={{ flex: 1 }} />
        {(['illustrated', 'generative', 'story'] as Bench[]).map((b) => (
          <button key={b} type="button" className={`btn tiny${bench === b ? ' pri' : ''}`} onClick={() => setBench(b)}>
            {b === 'illustrated' ? 'Illustrated' : b === 'generative' ? 'One clip' : 'Scene Builder'}
          </button>
        ))}
      </div>
      {error && <p className="err" role="alert" style={{ margin: 0 }}>{error}</p>}
      {note && <p className="ok" style={{ margin: 0 }}>{note}</p>}

      {bench === 'illustrated' && (
        <IllustratedBench brand={brand} talent={talent} beds={beds} onError={setError} creditsPerPicture={creditsPerPicture}
                          onMade={(m) => { setNote(m); setError(null); void loadClips(); }} />
      )}
      {bench === 'story' && <SceneBuilder brand={brand} talent={talent} onError={setError}
                                          prefill={prefill} onUsedPrefill={onUsedPrefill} />}

      {bench === 'generative' && (
        <p className="sub-dim" style={{ margin: 0 }} data-tool="video-generative-waits">
          A generated clip — a video model drawing every frame — is the next bench. Fifteen seconds of it costs three to nine whole episodes, so it comes with a model picker and a quote you confirm before the press, and it is not offered until one clip has been made and checked. The illustrated bench is here now, at about a picture per beat.
        </p>
      )}

      <div style={{ display: 'grid', gap: 6 }} data-tool="video-made">
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <h4 style={{ margin: 0 }}>Made here</h4>
          <span className="sub-dim" style={{ fontSize: 12 }}>{clips.length ? `${clips.length} in the library under Clips` : 'nothing yet'}</span>
          {building && !stalled && <span className="sub-dim" style={{ fontSize: 12 }}>· the render plane is drawing, speaking and cutting the newest — a few minutes, longer from cold</span>}
          {stalled && <span className="sub-dim" style={{ fontSize: 12 }}>· {GAVE_UP} <button type="button" className="btn tiny" onClick={() => { setStalled(false); void loadClips(); }}>Look again</button></span>}
        </div>
        {clips.slice(0, 12).map((c) => <ClipRow key={c.id} clip={c} onGone={() => void loadClips()} onError={setError} />)}
      </div>
    </section>
  );
}

function ClipRow({ clip, onGone, onError }: { clip: Clip; onGone: () => void; onError: (m: string) => void }) {
  const [urls, setUrls] = useState<{ video: string | null; poster: string | null } | null>(null);
  async function watch() {
    if (urls) { setUrls(null); return; }
    try { const c = await api.clip(clip.id); setUrls({ video: c.video, poster: c.poster }); } catch (e) { onError((e as Error).message); }
  }
  const seconds = clip.seconds != null ? Number(clip.seconds) : null;
  return (
    <div style={{ display: 'grid', gap: 6, padding: '4px 0', borderTop: '1px solid var(--line)' }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <span className="chip">{clip.how}</span>
        <span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={clip.name}>{clip.name}</span>
        <span className="sub-dim" style={{ fontSize: 12 }}>{clip.beats.length} {clip.beats.length === 1 ? 'beat' : 'beats'}{seconds ? ` · ${saySeconds(Math.round(seconds))}` : ''}</span>
        {clip.status === 'ready'
          ? <button type="button" className="btn tiny" onClick={() => void watch()}>{urls ? 'Close' : 'Watch'}</button>
          : clip.status === 'error'
            ? <span className="err" style={{ fontSize: 12 }} title={clip.error ?? ''}>{clip.error ? `failed: ${clip.error.slice(0, 120)}` : 'failed'}</span>
            : <span className="sub-dim" style={{ fontSize: 12 }}>building…</span>}
        <button type="button" className="btn tiny" title="Take it off this list. The clip and its pictures stay in the library."
                onClick={() => { api.deleteClip(clip.id).then(onGone).catch((e) => onError((e as Error).message)); }}>×</button>
      </div>
      {urls?.video && <video src={urls.video} poster={urls.poster ?? undefined} controls autoPlay style={{ width: '100%', maxWidth: 640, borderRadius: 6 }} />}
    </div>
  );
}

/** N beats: who says what, in what scene, how the camera moves over it. */
function IllustratedBench({ brand, talent, beds, onMade, onError, creditsPerPicture }: {
  /** From the price list, via the lab; the fallback until it loads. */
  creditsPerPicture: number | null;
  brand: Brand | null; talent: TalentRow[]; beds: Asset[]; onMade: (note: string) => void; onError: (m: string | null) => void;
}) {
  const [people, setPeople] = useState<string[]>([]);
  const [beats, setBeats] = useState<Draft[]>([blank()]);
  const [name, setName] = useState('');
  const [bed, setBed] = useState('');
  const [busy, setBusy] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const waited = useElapsed(startedAt);

  const cast = people.map((id) => talent.find((t) => t.id === id)).filter((t): t is TalentRow => Boolean(t));
  const unready = cast.filter((t) => t.portrait !== 'ready');
  const speakers = [...new Set(beats.map((b) => b.talentId).filter(Boolean))].map((id) => talent.find((t) => t.id === id)).filter((t): t is TalentRow => Boolean(t));
  const voiceless = speakers.filter((t) => !t.voice_id);
  const complete = beats.filter((b) => b.talentId && b.text.trim() && b.scene.trim().length >= 3);
  const chars = complete.reduce((n, b) => n + b.text.trim().length, 0);
  const why = people.length === 0 ? 'Pick who is in it'
    : unready.length ? `${unready.map((t) => t.name).join(', ')} ${unready.length === 1 ? 'has' : 'have'} no portrait yet — draw one on Talent first`
    : voiceless.length ? `${voiceless.map((t) => t.name).join(', ')} ${voiceless.length === 1 ? 'has' : 'have'} no voice yet — give them one on Talent first`
    : complete.length === 0 ? 'Write a beat: who says what, and the scene'
    : complete.length < beats.length ? 'A beat is missing its line, its speaker or its scene'
    : null;
  const canBuild = !why && !busy;

  function togglePerson(id: string) {
    setPeople((ps) => (ps.includes(id) ? ps.filter((x) => x !== id) : ps.length < MAX_PEOPLE ? [...ps, id] : ps));
    setBeats((bs) => bs.map((b) => (b.talentId === id && people.includes(id) ? { ...b, talentId: '' } : b)));
  }
  const setBeat = (i: number, patch: Partial<Draft>) => setBeats((bs) => bs.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  async function build() {
    setBusy(true); setStartedAt(Date.now()); onError(null);
    try {
      const r = await api.makeClip({
        brandId: brand?.id ?? null, people,
        beats: complete.map((b) => ({ talentId: b.talentId, text: b.text.trim(), scene: b.scene.trim(), motion: b.motion, ...(b.read.trim() ? { read: b.read.trim() } : {}) })),
        ...(name.trim() ? { name: name.trim() } : {}), ...(bed ? { bedAssetId: bed } : {}),
      });
      setBeats([blank(people[0])]); setName('');
      onMade(`Building — ${r.pictures} ${r.pictures === 1 ? 'picture' : 'pictures'} to draw, ${r.chars ? `${r.chars.toLocaleString()} characters of cloud voice` : 'local voices'}, then the cut. A few minutes; it lands in the library under Clips.`);
    } catch (e) { onError((e as Error).message); } finally { setBusy(false); setStartedAt(null); }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) minmax(0, 1fr)', gap: 14 }} data-tool="video-illustrated">
      {/* Who is in it. The cast, up to three: what the picture model takes as references, so every beat keeps their faces. */}
      <div style={{ display: 'grid', gap: 6, alignContent: 'start' }}>
        <span className="sub-dim">Who is in it <span style={{ fontSize: 11 }}>· up to {MAX_PEOPLE}</span></span>
        {talent.length === 0 && <p className="sub-dim" style={{ margin: 0, fontSize: 12 }}>Nobody in the vault yet — add talent on the Talent screen first. A clip is drawn from their portraits and spoken in their voices.</p>}
        {talent.map((t) => (
          <button key={t.id} type="button" className={`btn tiny${people.includes(t.id) ? ' pri' : ''}`} style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                  disabled={!people.includes(t.id) && people.length >= MAX_PEOPLE} onClick={() => togglePerson(t.id)}
                  title={t.portrait !== 'ready' ? 'no portrait yet — draw one on Talent' : t.voice_id ? 'portrait and voice ready' : 'no voice — can be in the picture, cannot speak'}>
            {t.name}<span className="sub-dim" style={{ marginLeft: 6, fontSize: 11 }}>{t.portrait !== 'ready' ? 'no portrait' : t.voice_id ? '' : 'no voice'}</span>
          </button>
        ))}
        <label className="sub-dim" style={{ fontSize: 12, marginTop: 6 }}>A bed under it
          <select value={bed} onChange={(e) => setBed(e.target.value)} style={{ width: '100%' }}>
            <option value="">none</option>
            {beds.map((a) => <option key={a.id} value={a.id}>{a.name ?? a.storage_key.split('/').pop()}</option>)}
          </select>
        </label>
      </div>

      <div style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
        {beats.map((b, i) => (
          <div key={i} style={{ display: 'grid', gap: 6, padding: 8, border: '1px solid var(--line)', borderRadius: 6 }}>
            <div className="row" style={{ gap: 6, alignItems: 'center' }}>
              <span className="sub-dim" style={{ fontSize: 12, width: 48 }}>Beat {i + 1}</span>
              <select value={b.talentId} onChange={(e) => setBeat(i, { talentId: e.target.value })} title="Who says it">
                <option value="">who speaks</option>
                {cast.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <select value={b.motion} onChange={(e) => setBeat(i, { motion: e.target.value as Draft['motion'] })} title="How the camera moves over the picture">
                {MOTIONS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
              <span style={{ flex: 1 }} />
              {beats.length > 1 && <button type="button" className="btn tiny" onClick={() => setBeats((bs) => bs.filter((_, j) => j !== i))}>×</button>}
            </div>
            <div className="row" style={{ gap: 6, alignItems: 'flex-start' }}>
              <textarea value={b.text} rows={2} maxLength={600} style={{ flex: 1 }} placeholder={i === 0 ? 'The line — "Pass the noodles. All of them."' : 'The next line'} onChange={(e) => setBeat(i, { text: e.target.value })} />
              <input value={b.read} maxLength={200} style={{ width: 140 }} placeholder="read it… greedily" title="How to read it — honoured by a voice that takes direction" onChange={(e) => setBeat(i, { read: e.target.value })} />
            </div>
            <input value={b.scene} maxLength={400} placeholder="The scene — the three of them at a wooden table, steam off the bowls, warm lamplight" onChange={(e) => setBeat(i, { scene: e.target.value })} />
          </div>
        ))}
        <div className="row wrap" style={{ gap: 8, alignItems: 'center' }}>
          <button type="button" className="btn tiny" disabled={beats.length >= MAX_BEATS} onClick={() => setBeats((bs) => [...bs, blank(bs[bs.length - 1]?.talentId ?? '')])}>+ beat</button>
          <input value={name} maxLength={200} placeholder="Name in the library (optional)" onChange={(e) => setName(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
          <button type="button" className="btn pri" disabled={!canBuild} data-move="video-clip-quote" title={why ?? `${complete.length} beats, ${chars} characters`} onClick={() => void build()}>
            {busy ? `Queuing… ${saySeconds(waited)}` : 'Build the clip'}
          </button>
        </div>
        <p className="sub-dim" style={{ margin: 0, fontSize: 12 }} data-tool="video-clip-quote">
          {why ? why
            : `${creditsPerPicture === null ? 'Reading the price list for' : `About ${(complete.length * creditsPerPicture).toLocaleString()} credits for`} ${complete.length} ${complete.length === 1 ? 'picture' : 'pictures'}, plus ${chars.toLocaleString()} characters of voice against this month's allowances. Each beat holds as long as its line takes to say — about ${saySeconds(Math.max(3, Math.round(chars / 15)))} in all.`}
        </p>
      </div>
    </div>
  );
}
